// WallpaperCanvas.tsx
import { useEffect, useRef, useState, useCallback } from "react";
import type { ColorData, SelectionBox } from "./type";

interface WallpaperCanvasProps {
  boxselectionBg: string; // 键盘背景图 (magicKeyboard.png) - 显示在选框内
  wallpaperCanvasWidth: number; // 画布宽度
  imgData: string; // 用户选择的图片/视频数据 - 显示在底层
  fastType: number; // 快速布局类型: 1-左上角, 2-左右居中, 3-宽度最大化, 4-高度最大化, 5-铺满
  /** 提色矩阵行数，默认 6；需与当前键盘布局行数一致（ES98=6 / X98=6） */
  colorMatrixRows?: number;
  /** 提色矩阵列数，默认 21；ES98(99)=21，X98(98)=19 */
  colorMatrixCols?: number;
  getColorData: (
    colorData: ColorData[][],
    isBrightnessChange?: boolean
  ) => void; // 获取颜色数据的回调
}

interface DragState {
  isDragging: boolean;
  startX: number;
  startY: number;
  initialBox: SelectionBox;
  dragHandle: "move" | "nw" | "ne" | "sw" | "se" | null;
}

// 固定画布高度
const CANVAS_HEIGHT = 495;

// 默认选框大小 656x180
const DEFAULT_BOX_WIDTH = 656;
const DEFAULT_BOX_HEIGHT = 180;

// 默认目标颜色矩阵大小（ES98 / 99 布局）
const DEFAULT_COLOR_MATRIX_ROWS = 6;
const DEFAULT_COLOR_MATRIX_COLS = 21;

// 饱和度增强系数 (1.3 = 增加30%饱和度)
const SATURATION_BOOST = 20;
// 亮度增强系数 (1.15 = 增加15%亮度)
const BRIGHTNESS_BOOST = 1;

// 黑色色值
const BLACK_COLOR = { r: 0, g: 0, b: 0 };

const WallpaperCanvas: React.FC<WallpaperCanvasProps> = ({
  boxselectionBg,
  wallpaperCanvasWidth,
  imgData,
  fastType,
  colorMatrixRows = DEFAULT_COLOR_MATRIX_ROWS,
  colorMatrixCols = DEFAULT_COLOR_MATRIX_COLS,
  getColorData,
}) => {
  const matrixRows = Math.max(
    1,
    Math.floor(Number(colorMatrixRows)) || DEFAULT_COLOR_MATRIX_ROWS
  );
  const matrixCols = Math.max(
    1,
    Math.floor(Number(colorMatrixCols)) || DEFAULT_COLOR_MATRIX_COLS
  );
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null); // 用户图片
  const keyboardImageRef = useRef<HTMLImageElement | null>(null); // 键盘图（显示在选框内）
  const animationRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageSize, setImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // 标记是否是首次加载或切换图片/视频源（不是帧更新）
  const isFirstLoadRef = useRef<boolean>(true);
  const prevImgDataSourceRef = useRef<string>(""); // 存储原始图片/视频源，用于检测真正的切换
  const prevFastTypeRef = useRef<number>(fastType);

  // 用于跟踪用户是否正在拖动
  const isDraggingRef = useRef<boolean>(false);

  // 选框状态
  const [selectionBox, setSelectionBox] = useState<SelectionBox>(() => {
    // 根据fastType初始化选框
    return getPresetBoxByType(wallpaperCanvasWidth, CANVAS_HEIGHT, fastType);
  });

  // 拖动状态
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialBox: selectionBox,
    dragHandle: null,
  });

  // 根据fastType获取预设选框
  function getPresetBoxByType(
    canvasW: number,
    canvasH: number,
    type: number
  ): SelectionBox {
    let x: number, y: number, width: number, height: number;

    switch (type) {
      case 1: // 左上角 - 656*180，位置左上角
        width = DEFAULT_BOX_WIDTH;
        height = DEFAULT_BOX_HEIGHT;
        x = 0;
        y = 0;
        break;

      case 2: // 左右居中 - 656*180，位置上下左右居中
        width = DEFAULT_BOX_WIDTH;
        height = DEFAULT_BOX_HEIGHT;
        x = (canvasW - width) / 2;
        y = (canvasH - height) / 2;
        break;

      case 3: // 宽度最大化 - 宽度占满，高度180，位置上下居中
        width = canvasW;
        height = DEFAULT_BOX_HEIGHT;
        x = 0;
        y = (canvasH - height) / 2;
        break;

      case 4: // 高度最大化 - 宽度656，高度占满，位置左右居中
        width = DEFAULT_BOX_WIDTH;
        height = canvasH;
        x = (canvasW - width) / 2;
        y = 0;
        break;

      case 5: // 铺满 - 宽高都占满整个画布
        width = canvasW;
        height = canvasH;
        x = 0;
        y = 0;
        break;

      default:
        width = DEFAULT_BOX_WIDTH;
        height = DEFAULT_BOX_HEIGHT;
        x = (canvasW - width) / 2;
        y = (canvasH - height) / 2;
    }

    return {
      x,
      y,
      width,
      height,
      sourceX: 0,
      sourceY: 0,
      sourceWidth: width,
      sourceHeight: height,
    };
  }

  // 同步拖动状态到ref
  useEffect(() => {
    isDraggingRef.current = dragState.isDragging;
  }, [dragState.isDragging]);

  // 加载键盘背景图（显示在选框内）
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = boxselectionBg;

    img.onload = () => {
      keyboardImageRef.current = img;
    };
  }, [boxselectionBg]);

  // 加载用户选择的图片（底层背景）
  useEffect(() => {
    if (!imgData) {
      imageRef.current = null;
      setImageSize(null);
      return;
    }

    // 检查是否是真正的新的图片/视频源（不是帧更新）
    const isVideoFrame = imgData.startsWith("data:image");
    const isNewSource =
      !prevImgDataSourceRef.current ||
      (!isVideoFrame && imgData !== prevImgDataSourceRef.current) ||
      (isVideoFrame && !prevImgDataSourceRef.current.startsWith("data:image"));

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imgData;

    img.onload = () => {
      imageRef.current = img;
      setImageSize({ width: img.width, height: img.height });

      // 只在首次加载或切换真正的图片/视频源时初始化选框位置
      // 并且用户没有在拖动
      if (!isDraggingRef.current && (isFirstLoadRef.current || isNewSource)) {
        console.log("切换图片/视频源，初始化选框位置");
        isFirstLoadRef.current = false;
        prevImgDataSourceRef.current = imgData;

        // 根据当前fastType重置选框
        const newBox = getPresetBoxByType(
          wallpaperCanvasWidth,
          CANVAS_HEIGHT,
          fastType
        );
        setSelectionBox(newBox);
      }
    };

    img.onerror = (err) => {
      console.error("图片加载失败:", err);
      imageRef.current = null;
      setImageSize(null);
    };
  }, [imgData, wallpaperCanvasWidth, fastType]);

  // 当 fastType 变化时更新选框
  useEffect(() => {
    // 防止重复触发
    if (prevFastTypeRef.current === fastType) return;
    prevFastTypeRef.current = fastType;

    console.log("切换快速布局模式，更新选框位置和大小");
    const newBox = getPresetBoxByType(
      wallpaperCanvasWidth,
      CANVAS_HEIGHT,
      fastType
    );
    setSelectionBox(newBox);
  }, [fastType, wallpaperCanvasWidth]);

  // 更新原始图片上的对应区域
  const updateSourceRegion = useCallback(
    (box: SelectionBox): SelectionBox => {
      if (!imageRef.current || !imageSize) {
        return {
          ...box,
          sourceX: 0,
          sourceY: 0,
          sourceWidth: box.width,
          sourceHeight: box.height,
        };
      }

      const scaleX = imageSize.width / wallpaperCanvasWidth;
      const scaleY = imageSize.height / CANVAS_HEIGHT;

      // 计算选框在原始图片上的对应区域
      let sourceX = Math.round(box.x * scaleX);
      let sourceY = Math.round(box.y * scaleY);
      let sourceWidth = Math.round(box.width * scaleX);
      let sourceHeight = Math.round(box.height * scaleY);

      // 限制在图片范围内
      sourceX = Math.max(0, Math.min(sourceX, imageSize.width));
      sourceY = Math.max(0, Math.min(sourceY, imageSize.height));
      sourceWidth = Math.min(sourceWidth, imageSize.width - sourceX);
      sourceHeight = Math.min(sourceHeight, imageSize.height - sourceY);

      return {
        ...box,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
      };
    },
    [imageSize, wallpaperCanvasWidth]
  );

  // 处理鼠标事件（拖动）
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // 缩放因子（画布实际像素到显示尺寸的缩放）
      const scaleX = wallpaperCanvasWidth / rect.width;
      const scaleY = CANVAS_HEIGHT / rect.height;

      const canvasX = mouseX * scaleX;
      const canvasY = mouseY * scaleY;

      // 检查是否点击在调整大小的手柄上
      const handleSize = 20;
      let dragHandle: "move" | "nw" | "ne" | "sw" | "se" | null = null;

      // 四个角的手柄
      if (
        Math.abs(canvasX - selectionBox.x) < handleSize &&
        Math.abs(canvasY - selectionBox.y) < handleSize
      ) {
        dragHandle = "nw";
      } else if (
        Math.abs(canvasX - (selectionBox.x + selectionBox.width)) <
          handleSize &&
        Math.abs(canvasY - selectionBox.y) < handleSize
      ) {
        dragHandle = "ne";
      } else if (
        Math.abs(canvasX - selectionBox.x) < handleSize &&
        Math.abs(canvasY - (selectionBox.y + selectionBox.height)) < handleSize
      ) {
        dragHandle = "sw";
      } else if (
        Math.abs(canvasX - (selectionBox.x + selectionBox.width)) <
          handleSize &&
        Math.abs(canvasY - (selectionBox.y + selectionBox.height)) < handleSize
      ) {
        dragHandle = "se";
      }
      // 检查是否点击在选框内部
      else if (
        canvasX >= selectionBox.x &&
        canvasX <= selectionBox.x + selectionBox.width &&
        canvasY >= selectionBox.y &&
        canvasY <= selectionBox.y + selectionBox.height
      ) {
        dragHandle = "move";
      }

      if (dragHandle) {
        setDragState({
          isDragging: true,
          startX: canvasX,
          startY: canvasY,
          initialBox: { ...selectionBox },
          dragHandle,
        });
        e.preventDefault();
      }
    },
    [selectionBox, wallpaperCanvasWidth]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragState.isDragging || !dragState.dragHandle || !canvasRef.current)
        return;

      const rect = canvasRef.current.getBoundingClientRect();
      const scaleX = wallpaperCanvasWidth / rect.width;
      const scaleY = CANVAS_HEIGHT / rect.height;

      const currentX = (e.clientX - rect.left) * scaleX;
      const currentY = (e.clientY - rect.top) * scaleY;

      const deltaX = currentX - dragState.startX;
      const deltaY = currentY - dragState.startY;

      const newBox = { ...dragState.initialBox };

      switch (dragState.dragHandle) {
        case "move": // 移动整个选框
          newBox.x = dragState.initialBox.x + deltaX;
          newBox.y = dragState.initialBox.y + deltaY;
          break;

        case "nw": // 左上角调整
          newBox.x = dragState.initialBox.x + deltaX;
          newBox.y = dragState.initialBox.y + deltaY;
          newBox.width = dragState.initialBox.width - deltaX;
          newBox.height = dragState.initialBox.height - deltaY;
          break;

        case "ne": // 右上角调整
          newBox.y = dragState.initialBox.y + deltaY;
          newBox.width = dragState.initialBox.width + deltaX;
          newBox.height = dragState.initialBox.height - deltaY;
          break;

        case "sw": // 左下角调整
          newBox.x = dragState.initialBox.x + deltaX;
          newBox.width = dragState.initialBox.width - deltaX;
          newBox.height = dragState.initialBox.height + deltaY;
          break;

        case "se": // 右下角调整
          newBox.width = dragState.initialBox.width + deltaX;
          newBox.height = dragState.initialBox.height + deltaY;
          break;
      }

      // 限制最小尺寸
      newBox.width = Math.max(50, newBox.width);
      newBox.height = Math.max(50, newBox.height);

      // 更新原始图片上的对应区域
      const updatedBox = updateSourceRegion(newBox);
      setSelectionBox(updatedBox);
    },
    [dragState, updateSourceRegion]
  );

  const handleMouseUp = useCallback(() => {
    setDragState((prev) => ({ ...prev, isDragging: false }));
  }, []);

  // 添加和移除全局鼠标事件监听
  useEffect(() => {
    if (dragState.isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState.isDragging, handleMouseMove, handleMouseUp]);

  // 绘制画布
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // 设置画布尺寸
    canvas.width = wallpaperCanvasWidth;
    canvas.height = CANVAS_HEIGHT;

    // 清除画布
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 1. 绘制底层 - 用户选择的图片（铺满整个画布）
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    } else {
      ctx.fillStyle = "#2d2d2d";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "#666";
      ctx.font = "14px Arial";
      ctx.textAlign = "center";
      ctx.fillText("请选择图片或视频", canvas.width / 2, canvas.height / 2);
    }

    // 2. 绘制半透明遮罩（选框外部变暗）
    ctx.fillStyle = "rgba(0, 0, 0, 0.01)";

    // 上边区域
    if (selectionBox.y > 0) {
      ctx.fillRect(0, 0, canvas.width, selectionBox.y);
    }
    // 左边区域
    if (selectionBox.x > 0) {
      ctx.fillRect(0, selectionBox.y, selectionBox.x, selectionBox.height);
    }
    // 右边区域
    if (selectionBox.x + selectionBox.width < canvas.width) {
      ctx.fillRect(
        selectionBox.x + selectionBox.width,
        selectionBox.y,
        canvas.width - (selectionBox.x + selectionBox.width),
        selectionBox.height
      );
    }
    // 下边区域
    if (selectionBox.y + selectionBox.height < canvas.height) {
      ctx.fillRect(
        0,
        selectionBox.y + selectionBox.height,
        canvas.width,
        canvas.height - (selectionBox.y + selectionBox.height)
      );
    }

    // 3. 绘制选框内的键盘图
    if (
      keyboardImageRef.current &&
      selectionBox.width > 0 &&
      selectionBox.height > 0
    ) {
      ctx.drawImage(
        keyboardImageRef.current,
        selectionBox.x,
        selectionBox.y,
        selectionBox.width,
        selectionBox.height
      );
    }

    // 4. 绘制选框边框
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 3;
    ctx.setLineDash([8, 4]);
    ctx.strokeRect(
      selectionBox.x,
      selectionBox.y,
      selectionBox.width,
      selectionBox.height
    );

    // 绘制四个角的调整手柄
    ctx.setLineDash([]);
    ctx.fillStyle = "#FFFFFF";
    const handleSize = 8;

    ctx.fillRect(
      selectionBox.x - handleSize / 2,
      selectionBox.y - handleSize / 2,
      handleSize,
      handleSize
    );
    ctx.fillRect(
      selectionBox.x + selectionBox.width - handleSize / 2,
      selectionBox.y - handleSize / 2,
      handleSize,
      handleSize
    );
    ctx.fillRect(
      selectionBox.x - handleSize / 2,
      selectionBox.y + selectionBox.height - handleSize / 2,
      handleSize,
      handleSize
    );
    ctx.fillRect(
      selectionBox.x + selectionBox.width - handleSize / 2,
      selectionBox.y + selectionBox.height - handleSize / 2,
      handleSize,
      handleSize
    );

    // 显示选框尺寸
    ctx.fillStyle = "#fff";
    ctx.font = "bold 12px Arial";
    ctx.shadowColor = "#000";
    ctx.shadowBlur = 4;
    ctx.fillText(
      `${Math.round(selectionBox.width)}x${Math.round(selectionBox.height)}`,
      selectionBox.x + 10,
      selectionBox.y - 10
    );
    ctx.shadowBlur = 0;
  }, [wallpaperCanvasWidth, selectionBox]);

  // 增强颜色饱和度
  const enhanceColor = useCallback(
    (r: number, g: number, b: number): { r: number; g: number; b: number } => {
      // 如果是黑色，直接返回黑色（避免增强黑色）
      if (r === 0 && g === 0 && b === 0) {
        return { r, g, b };
      }

      // 转换为HSL
      const rf = r / 255;
      const gf = g / 255;
      const bf = b / 255;

      const max = Math.max(rf, gf, bf);
      const min = Math.min(rf, gf, bf);
      let h = 0;
      let s = 0;
      const l = (max + min) / 2;

      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
          case rf:
            h = (gf - bf) / d + (gf < bf ? 6 : 0);
            break;
          case gf:
            h = (bf - rf) / d + 2;
            break;
          case bf:
            h = (rf - gf) / d + 4;
            break;
        }
        h /= 6;
      }

      // 增强饱和度
      s = Math.min(1, s * SATURATION_BOOST);

      // 增强亮度
      const enhancedL = Math.min(1, l * BRIGHTNESS_BOOST);

      // 转回RGB
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      let newR: number, newG: number, newB: number;

      if (s === 0) {
        newR = newG = newB = enhancedL;
      } else {
        const q =
          enhancedL < 0.5 ? enhancedL * (1 + s) : enhancedL + s - enhancedL * s;
        const p = 2 * enhancedL - q;
        newR = hue2rgb(p, q, h + 1 / 3);
        newG = hue2rgb(p, q, h);
        newB = hue2rgb(p, q, h - 1 / 3);
      }

      return {
        r: Math.round(Math.min(255, Math.max(0, newR * 255))),
        g: Math.round(Math.min(255, Math.max(0, newG * 255))),
        b: Math.round(Math.min(255, Math.max(0, newB * 255))),
      };
    },
    []
  );

  // 提取6x21颜色数据（支持部分溢出并增强颜色）
  const extractColorData = useCallback(() => {
    // 如果没有图片，返回全黑矩阵
    if (!imageRef.current || !imageSize) {
      const blackMatrix: ColorData[][] = [];
      for (let row = 0; row < matrixRows; row++) {
        const rowData: ColorData[] = [];
        for (let col = 0; col < matrixCols; col++) {
          rowData.push({ ...BLACK_COLOR });
        }
        blackMatrix.push(rowData);
      }
      getColorData(blackMatrix);
      return;
    }

    // 创建离屏画布，大小正好为6x21
    const offCanvas = document.createElement("canvas");
    offCanvas.width = matrixCols; // 21
    offCanvas.height = matrixRows; // 6
    const offCtx = offCanvas.getContext("2d");

    if (!offCtx) return;

    // 先填充黑色背景
    offCtx.fillStyle = "rgb(0, 0, 0)";
    offCtx.fillRect(0, 0, offCanvas.width, offCanvas.height);

    // 计算选框与画布的交集区域
    const intersectX = Math.max(0, selectionBox.x);
    const intersectY = Math.max(0, selectionBox.y);
    const intersectWidth =
      Math.min(selectionBox.x + selectionBox.width, wallpaperCanvasWidth) -
      intersectX;
    const intersectHeight =
      Math.min(selectionBox.y + selectionBox.height, CANVAS_HEIGHT) -
      intersectY;

    // 如果有交集区域，提取颜色
    if (intersectWidth > 0 && intersectHeight > 0) {
      // 计算交集区域在原始图片上的对应区域
      const scaleX = imageSize.width / wallpaperCanvasWidth;
      const scaleY = imageSize.height / CANVAS_HEIGHT;

      const sourceIntersectX = Math.round(intersectX * scaleX);
      const sourceIntersectY = Math.round(intersectY * scaleY);
      const sourceIntersectWidth = Math.round(intersectWidth * scaleX);
      const sourceIntersectHeight = Math.round(intersectHeight * scaleY);

      // 计算交集区域在6x21画布上的对应位置
      const destX = Math.round(
        ((intersectX - selectionBox.x) / selectionBox.width) * offCanvas.width
      );
      const destY = Math.round(
        ((intersectY - selectionBox.y) / selectionBox.height) * offCanvas.height
      );
      const destWidth = Math.round(
        (intersectWidth / selectionBox.width) * offCanvas.width
      );
      const destHeight = Math.round(
        (intersectHeight / selectionBox.height) * offCanvas.height
      );

      try {
        // 创建一个临时画布来提取交集区域的图片
        const tempCanvas = document.createElement("canvas");
        tempCanvas.width = sourceIntersectWidth;
        tempCanvas.height = sourceIntersectHeight;
        const tempCtx = tempCanvas.getContext("2d");

        if (tempCtx) {
          // 绘制交集区域的图片到临时画布
          tempCtx.drawImage(
            imageRef.current,
            sourceIntersectX,
            sourceIntersectY,
            sourceIntersectWidth,
            sourceIntersectHeight,
            0,
            0,
            tempCanvas.width,
            tempCanvas.height
          );

          // 将临时画布的内容绘制到最终画布的对应位置
          offCtx.drawImage(
            tempCanvas,
            0,
            0,
            tempCanvas.width,
            tempCanvas.height,
            destX,
            destY,
            destWidth,
            destHeight
          );
        }
      } catch (error) {
        console.error("绘制交集区域失败:", error);
      }
    }

    try {
      const imageData = offCtx.getImageData(
        0,
        0,
        offCanvas.width,
        offCanvas.height
      );
      const data = imageData.data;

      // 构建6行21列的颜色矩阵，并增强每个颜色
      const colorMatrix: ColorData[][] = [];

      for (let row = 0; row < offCanvas.height; row++) {
        const rowData: ColorData[] = [];
        for (let col = 0; col < offCanvas.width; col++) {
          const index = (row * offCanvas.width + col) * 4;
          const originalColor = {
            r: data[index],
            g: data[index + 1],
            b: data[index + 2],
          };

          // 增强颜色的饱和度和亮度
          const enhancedColor = enhanceColor(
            originalColor.r,
            originalColor.g,
            originalColor.b
          );
          rowData.push(enhancedColor);
        }
        colorMatrix.push(rowData);
      }

      // 回调返回6x21的增强颜色数据
      getColorData(colorMatrix);
    } catch (error) {
      console.error("提取颜色数据失败:", error);
      // 出错时返回黑色矩阵
      const blackMatrix: ColorData[][] = [];
      for (let row = 0; row < matrixRows; row++) {
        const rowData: ColorData[] = [];
        for (let col = 0; col < matrixCols; col++) {
          rowData.push({ ...BLACK_COLOR });
        }
        blackMatrix.push(rowData);
      }
      getColorData(blackMatrix);
    }
  }, [
    selectionBox,
    imageSize,
    wallpaperCanvasWidth,
    getColorData,
    enhanceColor,
    matrixRows,
    matrixCols,
  ]);

  // 动画循环
  useEffect(() => {
    // let frameCount = 0;
    let lastExtractTime = 0;
    const extractInterval = 100; // 每100ms提取一次（约10fps）

    const animate = (timestamp: number) => {
      drawCanvas();

      // 限流提取颜色数据
      if (timestamp - lastExtractTime > extractInterval) {
        extractColorData();
        lastExtractTime = timestamp;
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [drawCanvas, extractColorData]);

  // 当selectionBox变化时更新source区域
  useEffect(() => {
    setSelectionBox((prev) => updateSourceRegion(prev));
  }, [imageSize, updateSourceRegion]);

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      style={{
        width: "100%",
        height: "auto",
        position: "relative",
        cursor: dragState.isDragging
          ? "grabbing"
          : dragState.dragHandle === "move"
            ? "grab"
            : "crosshair",
        userSelect: "none",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

export default WallpaperCanvas;
